import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radar } from "lucide-react";
import { api } from "@/lib/api";
import { useRefetchOnActive } from "@/lib/useRefetch";
import { setActivityBadge } from "@/lib/badge";

export const ACTIVITY_SEEN_KEY = "sp_activity_seen";

// Full-width bottom bar — the single entry into the live Activity feed.
// Shown only on the Jobs (Dashboard) screen; every other screen relies on
// the native back gesture to return here. Shows an unread pulse + count for
// events newer than the last time the feed was opened.
export function ActivityBar() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/activity");
      const events = data.events || [];
      const seen = localStorage.getItem(ACTIVITY_SEEN_KEY) || "";
      const count = events.filter((e) => (e.at || "") > seen).length;
      setUnread(count);
      setActivityBadge(count);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useRefetchOnActive(load);

  const hasUnread = unread > 0;

  return (
    <button
      data-testid="activity-bar"
      onClick={() => navigate("/activity")}
      className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-center gap-2 border-t border-border bg-card/90 py-3.5 text-foreground backdrop-blur-xl transition-colors active:bg-muted md:hidden"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.875rem)" }}
    >
      <span className="relative flex">
        <Radar className="h-5 w-5 text-emerald-500" />
        {hasUnread && (
          <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-card" />
          </span>
        )}
      </span>
      <span className="text-sm font-semibold">Activity</span>
      {hasUnread && (
        <span
          data-testid="activity-unread-count"
          className="ml-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[11px] font-bold leading-none text-white"
        >
          {unread > 99 ? "99+" : unread} new
        </span>
      )}
    </button>
  );
}
